import moment from "moment";
import i18next from "i18next";
import { RxClock } from "react-icons/rx";
import React from "react";

export function isValidDate(d) {
	return d instanceof Date && !isNaN(d);
}

export function getBrowserTimeZone() {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch (_) {
		return undefined;
	}
}

export function formatMinutesToTime(minutes) {
	let hours = parseInt(minutes / 60);
	let mins = parseInt(minutes % 60);
	let sign = "";

	if (hours >= 0 && mins < 0) sign = "-";

	if (mins < 0) mins = mins * -1;

	if (mins < 10) mins = "0" + mins;

	return `${sign}${hours}h ${mins}'`;
}

export function formatSecondsToTime(seconds, format) {
	let minutes = parseInt(seconds / 60);
	let hours = parseInt(minutes / 60);

	let mins = minutes % 60;
	if (mins > 0 && mins < 10) mins = "0" + mins;

	let secs = seconds % 60;
	if (secs < 10) secs = "0" + secs;

	if (format === "hm") {
		return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
	} else if (format === "hms") {
		return `${hours}h ${mins}m ${secs}s`;
	} else return `${hours}:${mins}:${secs}`;
}

export function getDiffTime(start, end, type) {
	let totalTime = 0;
	if (type === undefined) type = "minutes";

	if (start != null && end != null) {
		let endTime = moment.utc(end).local();
		let startTime = moment.utc(start).local();

		totalTime = endTime.diff(startTime, type);

		if (isNaN(totalTime)) totalTime = 0;
	}

	return totalTime;
}

const BREAK_TYPE_PATTERN = /(^|[\s/_-])(pausa|descanso|descans|break|rest)(?=$|[\s/_-])/;

const getTimeRegistrationEmployeeKey = (time) => {
	const employee = time?.employee || {};
	return String(
		employee.id ??
			time?.employee_id ??
			`${employee.name || ""}|${employee.surname || ""}`
	);
};

const getTimeRegistrationDayKey = (time) => {
	const value = time?.day || time?.start;
	if (!value) return "";
	return String(value).slice(0, 10);
};

const getTimeRegistrationTimestamp = (value) => {
	const timestamp = value ? new Date(value).getTime() : NaN;
	return Number.isFinite(timestamp) ? timestamp : null;
};

const getTimeRegistrationDurationSeconds = (time) => {
	const start = getTimeRegistrationTimestamp(time?.start);
	const end = getTimeRegistrationTimestamp(time?.end);
	if (start === null || end === null) return 0;
	return Math.max(0, Math.floor((end - start) / 1000));
};

const isBreakTimeRegistration = (time) => {
	// Los descansos de jornada completa no forman parte del tiempo de pausa.
	// Una pausa explícita puede tener work_time=false, por lo que ese indicador
	// no sirve para distinguir ambos conceptos.
	if (time?.day_of_rest || time?.dayOfRest) return false;

	const typeName = String(time?.type?.name || time?.time_type || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase();

	return BREAK_TYPE_PATTERN.test(typeName);
};

export const getPlannedRestDays = (planAssignments) =>
	(planAssignments || []).flatMap((employeePlan) =>
		(employeePlan?.plans || []).flatMap((plan) => {
			const assignments = plan?.assignments || [];
			const hasRestAssignment = assignments.length === 0 || assignments.some(
				(assignment) =>
					["descanso", "descans", "rest", "time-off"].includes(
						String(assignment?.shift?.type || "")
							.normalize("NFD")
							.replace(/[\u0300-\u036f]/g, "")
							.toLowerCase()
					)
			);
			if (!hasRestAssignment || !plan?.day) return [];

			return [
				{
					day: String(plan.day).slice(0, 10),
					employee: employeePlan.employee,
					employee_id: employeePlan.employee?.id,
				},
			];
		})
	);

export const buildTimeRegistrationReportEntries = (times, restLabel, options = {}) => {
	const sortedTimes = [...(times || [])].sort((left, right) => {
		const employeeComparison = getTimeRegistrationEmployeeKey(left).localeCompare(
			getTimeRegistrationEmployeeKey(right)
		);
		if (employeeComparison !== 0) return employeeComparison;

		return (
			(getTimeRegistrationTimestamp(left?.start) || 0) -
			(getTimeRegistrationTimestamp(right?.start) || 0)
		);
	});

	const entries = [];
	let totalRestSeconds = 0;

	sortedTimes.forEach((time) => {
		const durationSeconds = getTimeRegistrationDurationSeconds(time);
		const explicitBreak = isBreakTimeRegistration(time);
		entries.push({
			kind: explicitBreak ? "rest" : "registration",
			day: getTimeRegistrationDayKey(time),
			start: time.start,
			end: time.end,
			typeName: time?.type?.name || time?.time_type || "-",
			durationSeconds,
			source: time,
		});

		if (explicitBreak) totalRestSeconds += durationSeconds;
	});

	if (options.plannedRestDays?.length) {
		const employeeKey = String(
			options.employeeId ??
				(sortedTimes.length > 0
					? getTimeRegistrationEmployeeKey(sortedTimes[0])
					: "")
		);
		const daysWithRegistrations = new Set(
			entries.map((entry) => entry.day).filter(Boolean)
		);
		const addedRestDays = new Set();
		options.plannedRestDays.forEach((restDay) => {
			if (employeeKey && getTimeRegistrationEmployeeKey(restDay) !== employeeKey) {
				return;
			}

			const dayKey = getTimeRegistrationDayKey(restDay);
			if (
				!dayKey ||
				daysWithRegistrations.has(dayKey) ||
				addedRestDays.has(dayKey)
			) return;

			entries.push({
				kind: "day-rest",
				day: dayKey,
				start: null,
				end: null,
				typeName: restLabel,
				durationSeconds: null,
			});
			addedRestDays.add(dayKey);
		});

		entries.sort((left, right) => {
			const dayComparison = String(left.day || "").localeCompare(
				String(right.day || "")
			);
			if (dayComparison !== 0) return dayComparison;
			return (
				(getTimeRegistrationTimestamp(left.start) || 0) -
				(getTimeRegistrationTimestamp(right.start) || 0)
			);
		});
	}

	return { entries, totalRestSeconds };
};

export function getDiffDays(start, end) {
	let totalDays = 0;

	if (end == null) {
		totalDays = 1;
	} else {
		const endTime = moment.utc(end).local();
		const startTime = moment.utc(start).local();
		totalDays = endTime.diff(startTime, "days") + 1;
	}

	return totalDays;
}

export function getWorkedTime(times, date) {
	var totalSeconds = getWorkedTimeRaw({ times, date, unit: "seconds" });
	const formatedTime = formatSecondsToTime(totalSeconds, "hm");

	return formatedTime;
}

export function getWorkedTimeRaw(params) {
	var totalTime = 0;
	const { times, date, unit, employee_id } = params;

	times
		.filter((time) => {
			if (employee_id && time.employee.id !== employee_id) return false;
			return date
				? moment.utc(time.start).local().format("DD/MM/YYYY") ===
				moment.utc(date).local().format("DD/MM/YYYY")
				: time;
		})
		.forEach((time) => {
			// just calc if it's work_time
			if (time.type !== undefined && time.type.work_time) {
				var getDiff = getDiffTime(time.start, time.end, unit);
				// console.log("getDiff", getDiff);
				// console.log("start", time.start);
				// console.log("end", time.end);
				totalTime += getDiff;
			}
		});

	return totalTime;
}

/** Desvío fichaje → texto compacto: +Ns, +45m, +1h 34m (nunca decimales tipo 1,57h). */
function formatBalanceShortFromDiffSec(diffSec) {
	if (diffSec === 0) return "0";
	const sign = diffSec > 0 ? "+" : "-";
	const abs = Math.abs(diffSec);
	if (abs < 60) {
		return `${sign}${abs}s`;
	}
	const hours = Math.floor(abs / 3600);
	const minutes = Math.floor((abs % 3600) / 60);
	if (hours === 0) {
		return `${sign}${minutes}m`;
	}
	if (minutes === 0) {
		return `${sign}${hours}h`;
	}
	return `${sign}${hours}h ${String(minutes).padStart(2, "0")}m`;
}

export function getBalanceHours(objective_hours, total_today) {
	// Convertir objective_hours a número, asegurando que la coma decimal se convierta en punto
	objective_hours = Number(objective_hours.toString().replace(",", "."));
	total_today = Number(total_today);

	// Verificar si la conversión fue exitosa
	if (isNaN(objective_hours) || isNaN(total_today)) {
		console.error(
			"Error: objective_hours o total_today no son números válidos."
		);
		return { short: "0", short_raw: 0, formated: "00h 00m" };
	}

	const objective_seconds = objective_hours * 3600;
	let totalSeconds = total_today - objective_seconds;
	const diffSec = Math.round(totalSeconds);

	const formatedTime =
		totalSeconds !== 0 && Math.abs(totalSeconds) < 60
			? `${totalSeconds > 0 ? "+" : "-"}${Math.abs(Math.round(totalSeconds))}s`
			: formatSecondsToTime(totalSeconds, "hm");

	const short_raw = diffSec / 3600;
	const short = diffSec === 0 ? "0" : formatBalanceShortFromDiffSec(diffSec);

	const balance_hours = {
		short,
		short_raw,
		formated: formatedTime,
	};

	return balance_hours;
}

// gets a time list and extracts total seconds
export function getWorkedTimeCounter(times) {
	var totalTime = 0;

	times.forEach((time) => {
		if (time.type !== undefined && time.type.work_time) {
			totalTime += getDiffTime(time.start, time.end, "seconds");
		}
	});

	const formatedTime = formatSecondsToTime(totalTime);
	return formatedTime;
}

// gets a time list and extracts total seconds
export function getTimeCounterByType(times, type) {
	var totalTime = 0;

	times.forEach((time) => {
		if (time.type !== undefined && time.type.id === type.id) {
			totalTime += getDiffTime(time.start, time.end, "minutes");
		}
	});

	return totalTime;
}

// Gets first start and last end from a list of time intervals
export function getFirstLastInterval(timeIntervals, isSelect = false) {
	if (!Array.isArray(timeIntervals) || timeIntervals.length === 0) {
		return "";
	}

	// Convert time (HH:mm) to minutes from start of the day
	const timeToMinutes = (time) => {
		const [hours, minutes] = time.split(":").map(Number);
		return hours * 60 + minutes;
	};

	// Convert minutes back to HH:mm format
	const minutesToTime = (minutes) => {
		const totalMinutes = minutes % (24 * 60); // Wrap around for cases > 1440
		const hours = Math.floor(totalMinutes / 60);
		const mins = totalMinutes % 60;
		return `${hours.toString().padStart(2, "0")}:${mins
			.toString()
			.padStart(2, "0")}`;
	};

	// Convertir desde UTC a la zona horaria local
	const convertedIntervals = timeIntervals.map(({ start, end }) => ({
		start: convertFromUTC(start),
		end: convertFromUTC(end),
	}));

	// Normalize intervals to handle crossing midnight
	const normalizedIntervals = convertedIntervals.map(({ start, end }) => {
		const startMinutes = timeToMinutes(start);
		let endMinutes = timeToMinutes(end);

		// Si la hora de fin es menor o igual que la de inicio, el turno cruza medianoche
		if (endMinutes <= startMinutes) {
			endMinutes += 24 * 60; // Agregar un día (1440 minutos)
		}

		return { start: startMinutes, end: endMinutes };
	});

	// Encontrar el inicio más temprano y el final más tarde
	let firstStart = normalizedIntervals[0].start;
	let lastEnd = normalizedIntervals[0].end;

	for (let i = 1; i < normalizedIntervals.length; i++) {
		if (normalizedIntervals[i].start < firstStart) {
			firstStart = normalizedIntervals[i].start;
		}
		if (normalizedIntervals[i].end > lastEnd) {
			lastEnd = normalizedIntervals[i].end;
		}
	}

	if (isSelect) {
		return `🕐 ${minutesToTime(firstStart)} - ${minutesToTime(lastEnd)}`;
	}

	// Convertir resultados de vuelta a HH:mm formato local
	return (
		<span className="shift-times">
			<span className="clock-icon"><RxClock /></span> {minutesToTime(firstStart)}
			<span className="separador">-</span>
			{minutesToTime(lastEnd)}
		</span>
	);
}

// gets a time list and extracts total seconds
export function calculateWorkTime(intervals) {
	let totalSeconds = 0;

	intervals.forEach((interval) => {
		if (interval.time_type.work_time) {
			// Convertimos la hora desde UTC a la zona horaria local
			const localStart = convertFromUTC(interval.start);
			const localEnd = convertFromUTC(interval.end);

			const [startHours, startMinutes] = localStart.split(":").map(Number);
			const [endHours, endMinutes] = localEnd.split(":").map(Number);

			let startInSeconds = startHours * 3600 + startMinutes * 60;
			let endInSeconds = endHours * 3600 + endMinutes * 60;

			// Si la hora de fin es menor que la de inicio, significa que el turno cruza medianoche
			if (endInSeconds < startInSeconds) {
				endInSeconds += 24 * 3600; // Añadir 24 horas en segundos
			}

			totalSeconds += endInSeconds - startInSeconds;
		}
	});

	return totalSeconds;
}

// Normaliza intervalos 24h no laborales (work_time=false y start===end) para UI
export function fixStartEndIntervals(time_intervals) {
	if (!time_intervals || !Array.isArray(time_intervals)) {
		return time_intervals;
	}

	return time_intervals.map((interval) => {
		const is24hNonWork =
			interval &&
			interval.time_type &&
			interval.time_type.work_time === false &&
			interval.start === interval.end;

		if (!is24hNonWork) return interval;

		const start = interval.start;

		let correctedEnd;
		const startHms = moment(start, ["HH:mm:ss", "HH:mm"], true);
		if (startHms.isValid()) {
			const fmt = start.length === 5 ? "HH:mm" : "HH:mm:ss";
			correctedEnd = startHms.clone().subtract(1, "minute").format(fmt);
		} else {
			const startIso = moment(start);
			if (startIso.isValid()) {
				correctedEnd = startIso.clone().subtract(1, "minute").toISOString();
			} else {
				return interval;
			}
		}

		const adjusted = {
			...interval,
			end: correctedEnd,
		};

		try {
			// Log de diagnóstico controlado
			// eslint-disable-next-line no-console
			console.debug(
				"[fixStartEndIntervals] Ajuste aplicado (work_time=false, 24h)",
				{ start, newEnd: correctedEnd, interval: { ...interval } }
			);
		} catch (_) { }

		return adjusted;
	});
}

/**
 * Cachés de conversión horaria.
 *
 * `convertToUTC` / `convertFromUTC` son puras respecto al string de entrada y al
 * offset local vigente, pero cada llamada cuesta 3 parseos de moment. La rejilla
 * de la vista Horas las invoca decenas de miles de veces por carga
 * (empleado × día × hora × intervalo), y era el mayor coste de CPU del plan.
 *
 * Las cachés se vacían si cambia el offset local, para no servir valores
 * obsoletos al cruzar un cambio de horario con la pestaña abierta.
 */
const TIMEZONE_OFFSET_RECHECK_MS = 60000;
const toUTCCache = new Map();
const fromUTCCache = new Map();
let cachedTimezoneOffset = null;
let lastTimezoneCheck = 0;

/**
 * Revalida el offset como máximo una vez por minuto: `new Date()` en cada
 * llamada volvía a ser medible con decenas de miles de conversiones por carga.
 */
function ensureTimeConversionCaches() {
	const now = Date.now();
	if (
		cachedTimezoneOffset !== null &&
		now - lastTimezoneCheck < TIMEZONE_OFFSET_RECHECK_MS
	) {
		return;
	}
	lastTimezoneCheck = now;
	const offset = new Date().getTimezoneOffset();
	if (offset !== cachedTimezoneOffset) {
		cachedTimezoneOffset = offset;
		toUTCCache.clear();
		fromUTCCache.clear();
	}
}

// Convertir la hora ingresada a UTC antes de guardarla en la base de datos
export function convertToUTC(time) {
	if (!time) return ""; // Manejar valores vacíos o inválidos

	const cacheable = typeof time === "string";
	if (cacheable) {
		ensureTimeConversionCaches();
		const hit = toUTCCache.get(time);
		if (hit !== undefined) return hit;
	}

	const value = moment(time, "HH:mm", true).isValid()
		? moment.utc(moment(time, "HH:mm")).format("HH:mm[Z]")
		: "";

	if (cacheable) toUTCCache.set(time, value);
	return value;
}

// Convertir de UTC a la zona local del usuario antes de mostrarlo en la UI
export function convertFromUTC(time) {
	if (!time) return "";

	const cacheable = typeof time === "string";
	if (cacheable) {
		ensureTimeConversionCaches();
		const hit = fromUTCCache.get(time);
		if (hit !== undefined) return hit;
	}

	const value = moment(time, "HH:mmZ", true).isValid()
		? moment.utc(time, "HH:mmZ").local().format("HH:mm")
		: "";

	if (cacheable) fromUTCCache.set(time, value);
	return value;
}

/**
 * Minutos desde medianoche de un reloj `HH:mm`, `HH:mm:ss` o day-view `24:mm`.
 * Devuelve `null` si el string no es un reloj válido, para que quien llame
 * pueda tratarlo como "sin solape" igual que un moment inválido.
 */
export function parseClockMinutes(time) {
	if (typeof time !== "string" || time.length < 4) return null;

	const colon = time.indexOf(":");
	if (colon < 1) return null;

	const hours = Number(time.slice(0, colon));
	const minutes = Number(time.slice(colon + 1, colon + 3));

	if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
	// 24:mm es notación day-view (fin del día), no una hora de reloj real.
	if (hours < 0 || hours > 24 || minutes < 0 || minutes > 59) return null;

	return hours * 60 + minutes;
}


// export function convertUTCToLocalHourString(utcHour, date) {
// 	const m = moment.tz(`${date}T${utcHour}`, "Europe/Madrid");
// 	const hour = m.hour().toString().padStart(2, "0");
// 	const min = m.minute().toString().padStart(2, "0");
// 	return `${hour}:${min}`; 
// }

// Formatear fecha y hora en formato relativo (hoy, ayer, etc.)
export function formatRelativeDateTime(dateString) {
	if (!dateString) return "";

	// Convertir la fecha UTC a hora local
	const date = moment.utc(dateString).local();
	const today = moment().startOf("day");
	const yesterday = moment().subtract(1, "days").startOf("day");

	// Formatear la hora en formato 24h
	const timeFormat = date.format("HH:mm");

	// Determinar si debe usar "a la" (para 1:XX) o "a las" (resto de horas)
	const hour = parseInt(date.format("H"));
	const timePrefix = hour === 1 ? "a la" : "a las";

	// Determinar si es hoy, ayer o un día anterior
	if (date.isSame(today, "day")) {
		return `hoy ${timePrefix} ${timeFormat}`;
	} else if (date.isSame(yesterday, "day")) {
		return `ayer ${timePrefix} ${timeFormat}`;
	} else {
		// Para fechas anteriores, mostrar la fecha completa
		return `el ${date.format("DD/MM/YYYY")} ${timePrefix} ${timeFormat}`;
	}
}

// **
//  * Agrupa datos de asignaciones por semana para gráficos lineales
//  * @param {Array} data - Datos de asignaciones diarias
//  * @returns {Object} - Series y etiquetas para el gráfico
//  */
export const groupDataByWeek = (data) => {
	if (!data || data.length === 0) return { series: [], labels: [] };

	// Encontrar el rango de fechas real en los datos
	let minDate = null;
	let maxDate = null;

	// Primero, encontrar el rango de fechas real en los datos
	data.forEach((item) => {
		if (!item.day) return;

		const date = moment(item.day);
		if (!minDate || date.isBefore(minDate)) {
			minDate = date.clone();
		}
		if (!maxDate || date.isAfter(maxDate)) {
			maxDate = date.clone();
		}
	});

	// Si no hay fechas válidas, retornar arrays vacíos
	if (!minDate || !maxDate) {
		return { series: [], labels: [] };
	}

	// Agrupar datos por semana
	const weeklyData = data.reduce((acc, item) => {
		// Solo procesar elementos con datos válidos
		if (!item.day) return acc;

		const date = moment(item.day);
		// Calcular el número de semana ISO correctamente
		const weekNumber = date.isoWeek();
		const year = date.isoWeekYear(); // Usar isoWeekYear en lugar de year()
		const weekKey = `${year}-W${weekNumber}`;

		if (!acc[weekKey]) {
			acc[weekKey] = {
				weekNumber,
				year,
				needed_hours: 0,
				current_hours: 0,
				target_hours: 0,
				missing_hours: 0,
				days: 0,
				startDate: date.clone().startOf("isoWeek"),
				endDate: date.clone().endOf("isoWeek"),
			};
		}

		// Sumar horas para esta semana
		acc[weekKey].needed_hours += parseFloat(item.needed_hours) || 0;
		acc[weekKey].current_hours += parseFloat(item.current_hours) || 0;
		acc[weekKey].target_hours += parseFloat(item.target_hours) || 0;
		acc[weekKey].missing_hours += parseFloat(item.missing_hours) || 0;
		acc[weekKey].days += 1;

		return acc;
	}, {});

	// Convertir a arrays para ApexCharts y ordenar correctamente por número de semana y año
	const weeks = Object.keys(weeklyData).sort((a, b) => {
		const weekA = weeklyData[a];
		const weekB = weeklyData[b];

		// Primero ordenar por año
		if (weekA.year !== weekB.year) {
			return weekA.year - weekB.year;
		}

		// Luego por número de semana
		return weekA.weekNumber - weekB.weekNumber;
	});

	// Filtrar semanas que están dentro del rango de fechas
	const filteredWeeks = weeks.filter((week) => {
		const weekData = weeklyData[week];
		const weekStart = weekData.startDate;
		const weekEnd = weekData.endDate;

		// Una semana está dentro del rango si:
		// 1. La semana comienza dentro del rango, o
		// 2. La semana termina dentro del rango, o
		// 3. La semana contiene todo el rango
		return (
			(weekStart.isSameOrAfter(minDate, "day") && weekStart.isSameOrBefore(maxDate, "day")) ||
			(weekEnd.isSameOrAfter(minDate, "day") && weekEnd.isSameOrBefore(maxDate, "day")) ||
			(weekStart.isBefore(minDate, "day") && weekEnd.isAfter(maxDate, "day"))
		);
	});

	const series = [
		{
			name: "Necesidades de personal",
			data: filteredWeeks.map((week) => weeklyData[week].needed_hours),
			color: "#FFB020", // Amarillo
		},
		{
			name: "Horas trabajadas",
			data: filteredWeeks.map((week) => weeklyData[week].current_hours),
			color: "#0a8a0a", // Verde
		},
		{
			name: i18next.t("plan.tooltip.targetHoursTitle"),
			data: filteredWeeks.map((week) => weeklyData[week].target_hours),
			color: "#5b49fe", // Azul
		},
		{
			name: "Horas sin cubrir",
			data: filteredWeeks.map((week) => weeklyData[week].missing_hours),
			color: "#FF0000", // Rojo
		},
	];

	// Verificar si hay múltiples años en los datos
	const years = [
		...new Set(filteredWeeks.map((week) => weeklyData[week].year)),
	];
	const hasMultipleYears = years.length > 1;

	// Formatear etiquetas de semana para el eje X
	const labels = filteredWeeks.map((week) => {
		const { weekNumber, year } = weeklyData[week];
		// Si hay múltiples años, incluir el año en la etiqueta
		return hasMultipleYears
			? `Semana ${weekNumber} (${year})`
			: `Semana ${weekNumber}`;
	});

	return { series, labels };
};

/**
 * Agrupa datos de asignaciones por semana y día para gráficos de barras
 * @param {Array} data - Datos de asignaciones diarias
 * @returns {Array} - Datos agrupados por semana y día
 */
export const groupDataByWeekAndDay = (data) => {
	if (!data || data.length === 0) return [];

	// Encontrar el rango de fechas real en los datos
	let minDate = null;
	let maxDate = null;

	// Primero, encontrar el rango de fechas real en los datos
	data.forEach((item) => {
		if (!item.day) return;

		const date = moment(item.day);
		if (!minDate || date.isBefore(minDate)) {
			minDate = date.clone();
		}
		if (!maxDate || date.isAfter(maxDate)) {
			maxDate = date.clone();
		}
	});

	// Si no hay fechas válidas, retornar array vacío
	if (!minDate || !maxDate) {
		return [];
	}

	// Agrupar datos por semana
	const weeklyData = data.reduce((acc, item) => {
		if (!item.day) return acc;

		const date = moment(item.day);
		const weekNumber = date.isoWeek(); // Usar isoWeek para consistencia con groupDataByWeek
		const year = date.year();
		const weekKey = `${year}-W${weekNumber}`;
		const dayOfWeek = date.day(); // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado
		const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Ajustar para que 0 = Lunes, ..., 6 = Domingo

		if (!acc[weekKey]) {
			acc[weekKey] = {
				weekNumber,
				year,
				days: Array(7)
					.fill()
					.map(() => ({
						needed_hours: 0,
						current_hours: 0,
						target_hours: 0,
						missing_hours: 0,
					})),
				// Guardar la fecha de inicio y fin de la semana para filtrado posterior
				startDate: moment().year(year).isoWeek(weekNumber).startOf("isoWeek"),
				endDate: moment().year(year).isoWeek(weekNumber).endOf("isoWeek"),
			};
		}

		// Sumar horas para este día de la semana
		acc[weekKey].days[adjustedDayOfWeek].needed_hours += item.needed_hours || 0;
		acc[weekKey].days[adjustedDayOfWeek].current_hours +=
			item.current_hours || 0;
		acc[weekKey].days[adjustedDayOfWeek].target_hours += item.target_hours || 0;
		acc[weekKey].days[adjustedDayOfWeek].missing_hours +=
			item.missing_hours || 0;

		return acc;
	}, {});

	// Ordenar semanas correctamente por año y número de semana
	const sortedWeeks = Object.keys(weeklyData).sort((a, b) => {
		const weekA = weeklyData[a];
		const weekB = weeklyData[b];

		// Primero ordenar por año
		if (weekA.year !== weekB.year) {
			return weekA.year - weekB.year;
		}

		// Luego por número de semana
		return weekA.weekNumber - weekB.weekNumber;
	});

	// Filtrar semanas que están dentro del rango de fechas
	const filteredWeeks = sortedWeeks.filter((week) => {
		const weekData = weeklyData[week];

		// Verificar si esta semana está dentro del rango de fechas seleccionado
		// Una semana está dentro del rango si:
		// 1. Su inicio está dentro del rango, o
		// 2. Su fin está dentro del rango, o
		// 3. Contiene todo el rango (su inicio es antes y su fin es después)
		return (
			(weekData.startDate.isSameOrAfter(minDate, "day") &&
				weekData.startDate.isSameOrBefore(maxDate, "day")) ||
			(weekData.endDate.isSameOrAfter(minDate, "day") &&
				weekData.endDate.isSameOrBefore(maxDate, "day")) ||
			(weekData.startDate.isBefore(minDate, "day") &&
				weekData.endDate.isAfter(maxDate, "day"))
		);
	});

	// Verificar si hay múltiples años en los datos
	const years = [
		...new Set(filteredWeeks.map((week) => weeklyData[week].year)),
	];
	const hasMultipleYears = years.length > 1;

	// Preparar datos para cada semana
	return filteredWeeks.map((weekKey) => {
		const { weekNumber, year, days } = weeklyData[weekKey];

		// Crear series para el gráfico de barras
		const series = [
			{
				name: "Necesidades de personal",
				data: days.map((day) => day.needed_hours),
				color: "#FFB020", // Amarillo
			},
			{
				name: "Horas trabajadas",
				data: days.map((day) => day.current_hours),
				color: "#0a8a0a", // Verde
			},
			{
				name: i18next.t("plan.tooltip.targetHoursTitle"),
				data: days.map((day) => day.target_hours),
				color: "#5b49fe", // Azul
			},
			{
				name: "Horas sin cubrir",
				data: days.map((day) => day.missing_hours),
				color: "#FF0000", // Rojo
			},
		];

		return {
			weekNumber,
			year,
			hasMultipleYears,
			series,
		};
	});
};

// ─── Staffing: detección de necesidades fraccionadas ─────────────────────────
//
// Contexto:
//   La vista de Funciones (Plan.js > getBloqueStaffingLevel) muestra una única
//   tabla de necesidades (PlanStaffingLevels). Sin embargo, existen dos tipos
//   de necesidades que tienen semánticas distintas:
//
//     1. Necesidades de TURNO:  el turno dura más de 60 min (ej. "Mañana 9-14h").
//        Representan cuántas personas se necesitan para ese turno completo.
//
//     2. Necesidades FRACCIONADAS: el turno dura ≤ 60 min y es múltiplo exacto
//        de 15 min (ej. "0900" 10:00-11:00, "0930" 10:30-11:00, "CIERRE2315" 22:00-22:15).
//        Representan cobertura horaria, consistentes con el parámetro
//        `fractionMinutes=15` que usa staffingLevelsService.getStaffingLevelHourlyAssignments.
//
//   El objetivo es separar ambos tipos para renderizarlos en tablas distintas.
//   Ver: Plan.js > getBloqueStaffingLevel para el punto de uso.
//
// Decisión de diseño (2026-06-29):
//   Se usa la Opción 2 (duración + múltiplo de fracción) en lugar de la
//   Opción 1 (solo duración ≤ 60 min) porque es más robusta: un turno real de
//   exactamente 60 min no quedaría mal clasificado si no cumple el múltiplo.
//   Opción 3 (flag de backend) queda pendiente como mejora futura.
//
// Constantes configurables:
const STAFFING_FRACTION_MINUTES = 15; // granularidad del endpoint /fractioned
const STAFFING_FRACTION_MAX_MINUTES = 60; // umbral máximo para considerar fraccionado

/**
 * Calcula la duración total en minutos de todos los intervalos de un turno.
 * No convierte desde UTC porque solo necesitamos la diferencia (el offset cancela).
 * Maneja correctamente turnos que cruzan la medianoche.
 *
 * @param {Array} timeIntervals - array de { start: "HH:mmZ", end: "HH:mmZ" }
 * @returns {number} duración total en minutos
 */
export function getShiftDurationMinutes(timeIntervals) {
	if (!Array.isArray(timeIntervals) || timeIntervals.length === 0) return 0;

	return timeIntervals.reduce((total, interval) => {
		const [sh, sm] = interval.start.replace("Z", "").split(":").map(Number);
		const [eh, em] = interval.end.replace("Z", "").split(":").map(Number);
		let diff = (eh * 60 + em) - (sh * 60 + sm);
		if (diff < 0) diff += 1440; // turno que cruza medianoche
		return total + diff;
	}, 0);
}

/**
 * Determina si un turno representa una necesidad fraccionada (horaria).
 *
 * Un turno es "fraccionado" cuando su duración total cumple:
 *   - es > 0 minutos
 *   - es ≤ STAFFING_FRACTION_MAX_MINUTES (60 min)
 *   - es múltiplo exacto de STAFFING_FRACTION_MINUTES (15 min)
 *     → cubre: 15, 30, 45, 60 minutos
 *
 * Ejemplos del plan T012:
 *   "0900" (60 min)  → true   "CIERRE2315" (15 min) → true
 *   "0930" (30 min)  → true   "Turno TA"   (8h)     → false
 *
 * @param {Object} shift - objeto turno con propiedad time_intervals
 * @returns {boolean}
 */
export function isFractionalShift(shift) {
	if (!shift || !Array.isArray(shift.time_intervals)) return false;
	const duration = getShiftDurationMinutes(shift.time_intervals);
	return (
		duration > 0 &&
		duration <= STAFFING_FRACTION_MAX_MINUTES &&
		duration % STAFFING_FRACTION_MINUTES === 0
	);
}
